import { Alert, Badge } from "@/shared/components/MDX/MDXComponents";

# 🏭 راهنمای ایجاد و مدیریت خطوط تولید

پلتفرم کارخانه‌سازی میمزاد (Mimzad Manufacturing Platform) — سند راهنمای کاربری خطوط تولید

<Badge type="gold">سند استاندارد کاربری</Badge> <Badge>راهنمای جامع</Badge>

## 📋 فهرست مطالب و دسترسی سریع

1. [مقدمه و ساختار خطوط تولید](#sec-intro)
2. [ایجاد خط تولید جدید](#sec-step1)
3. [تعریف ایستگاه‌های کاری (Work Centers)](#sec-step2)
4. [افزودن و نگاشت ماشین‌آلات](#sec-step3)
5. [نمایش گرافیکی و فیلترها](#sec-step4)

---

## 1. مقدمه و ساختار خطوط تولید

در سیستم میمزاد، ساختار فیزیکی کارخانه از طریق **خطوط تولید (Production Lines)**، **ایستگاه‌های کاری (Work Centers)** و **ماشین‌آلات (Machines)** مدل‌سازی می‌شود.
شما در این بخش می‌توانید توالی دستگاه‌ها را تعیین کرده و ظرفیت و چیدمان کارگاه را به صورت گرافیکی مشاهده و مدیریت کنید.

<Alert type="info">
  **ساختار درختی:** هر خط تولید شامل یک یا چند ایستگاه کاری است، و هر ایستگاه کاری می‌تواند شامل چندین ماشین‌آلات و تجهیزات باشد.
</Alert>

---

## 2. مرحله اول: ایجاد خط تولید جدید

برای ثبت یک خط تولید جدید:

1. در بالای صفحه، روی دکمه **«➕ افزودن خط تولید»** کلیک کنید.
2. در فرم بازشده، **کد خط تولید** (یکتای سیستمی) و **عنوان خط تولید** (نام نمایشی) را وارد کنید.
3. با تایید فرم، خط تولید شما در لیست اضافه می‌شود.

---

## 3. مرحله دوم: تعریف ایستگاه‌های کاری (Work Centers)

ایستگاه‌های کاری، گلوگاه‌های اصلی در هر خط تولید هستند که عملیات‌های ساخت در آن‌ها انجام می‌پذیرد.
ایستگاه‌های کاری به خطوط تولید تخصیص می‌یابند. برای ویرایش توالی (Sequence) یا مدیریت ایستگاه‌ها، کافی است بر روی هر خط تولید کلیک کنید تا ایستگاه‌های متصل به آن باز شوند.

---

## 4. مرحله سوم: افزودن ماشین‌آلات (Machines)

ماشین‌آلات، تجهیزات فیزیکی هستند که درون هر ایستگاه کاری مستقر می‌شوند. برای تخصیص یک ماشین:

1. در بالای صفحه روی **«➕ افزودن ماشین‌آلات»** کلیک کنید.
2. ایستگاه کاری مورد نظر برای استقرار ماشین را انتخاب کنید.
3. **کد ماشین** (پلاک اموال یا کد یونیک) و **عنوان ماشین** را بنویسید.
4. با تایید فرم، ماشین به ایستگاه کاری مربوطه اضافه شده و در ساختار درختی نمایش داده می‌شود.

### کاتالوگ موجودیت‌های خط تولید

| عنوان | کد سیستم | توضیحات و نقش |
| :--- | :--- | :--- |
| **خط تولید (Production Line)** | `Line` | جریان کلی ساخت یک محصول یا گروه محصول. |
| **ایستگاه کاری (Work Center)** | `WorkCenter` | مکان فیزیکی یا گروهی از اپراتورها که یک مرحله از ساخت را انجام می‌دهند. |
| **ماشین‌آلات (Machine)** | `Machine` | تجهیزات و ابزارآلاتی که درون ایستگاه‌های کاری نصب شده‌اند. |

---

## 5. نمایش گرافیکی و فیلترها

در بالای صفحه، نوار جستجویی وجود دارد که به شما امکان می‌دهد خطوط تولید را بر اساس کد یا نام فیلتر کنید. همچنین سیستم به صورت در لحظه، تعداد کل خطوط تولید، ایستگاه‌های کاری و ماشین‌آلات را پایش کرده و به شما نمایش می‌دهد.

<Alert type="tip">
  شما می‌توانید روی هر خط تولید کلیک کنید تا نمای درختی باز شود و سلسله مراتب ماشین‌آلات زیرمجموعه‌ی هر ایستگاه کاری را مشاهده کنید.
</Alert>
